"use client";

import React from "react";
import { Mail, Phone, MapPin, Loader, Clock, ChevronDown, ArrowRight } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const INQUIRY_OPTIONS = ["Loft Conversion Specialist", "Rear & Side Extensions", "Double Storey Extensions", "Complete House Refurbishment", "Kitchen & Bathroom Renovation"];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="85742" className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in duration-500">
        <div className="size-12 rounded-none bg-primary flex items-center justify-center mb-4">
          <Mail className="size-6 text-primary-foreground" />
        </div>
        <h3 className="text-2xl font-bold tracking-widest font-sans uppercase text-white">
          INQUIRY RECEIVED
        </h3>
        <p className="text-[#B0B0B0] max-w-md font-sans">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-none border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300">
          SUBMIT ANOTHER REQUEST
        </Button>
      </div>;
  }
  const fieldStyles = "bg-[#FFFFFF] border-[#E5E7EB] border rounded-md placeholder:text-[#6B7280] text-[#1A1A1A] focus-visible:ring-0 focus-visible:border-gray-400 transition-all";
  return <Form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="name" rules={{
        required: "Full name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase text-xs tracking-widest font-bold text-[#FFFFFF]">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="JOHN DOE" className={fieldStyles} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase text-xs tracking-widest font-bold text-[#FFFFFF]">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="CONTACT@DOMAIN.COM" className={fieldStyles} {...field} />
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase text-xs tracking-widest font-bold text-[#FFFFFF]">Phone Number (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+44 20 XXXX XXXX" className={fieldStyles} {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px] uppercase text-[#B0B0B0]">
                Required for site visit coordination.
              </FormDescription>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "Please select a project type"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase text-xs tracking-widest font-bold text-[#FFFFFF]">Project Type</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className={`flex h-9 w-full rounded-md border border-[#E5E7EB] bg-[#FFFFFF] px-3 py-1 text-base text-[#1A1A1A] shadow-none transition-colors focus-visible:outline-none focus-visible:border-gray-400 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none font-sans`} {...field}>
                    <option value="" disabled className="bg-white">
                      SELECT PROJECT CATEGORY
                    </option>
                    {INQUIRY_OPTIONS.map((option, i) => <option key={i} value={option} data-index={i} className="bg-white">
                        {option.toUpperCase()}
                      </option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-[#6B7280] pointer-events-none" />
                </div>
              </FormControl>
              <FormMessage className="font-mono text-[10px]" />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" rules={{
      required: "Project location is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase text-xs tracking-widest font-bold text-[#FFFFFF]">Project Location / Subject</FormLabel>
            <FormControl>
              <Input placeholder="E.G. WEMBLEY REAR EXTENSION" className={fieldStyles} {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Project details are required",
      minLength: {
        value: 20,
        message: "Details must be at least 20 characters"
      },
      maxLength: {
        value: 2000,
        message: "Details cannot exceed 2000 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase text-xs tracking-widest font-bold text-[#FFFFFF]">Project Specification</FormLabel>
            <FormControl>
              <Textarea placeholder="DESCRIBE YOUR VISION, DIMENSIONS, AND REQUIREMENTS IN DETAIL..." className={`min-h-[150px] resize-y ${fieldStyles}`} {...field} />
            </FormControl>
            <FormMessage className="font-mono text-[10px]" />
          </FormItem>} />

      {submitError && <div className="p-4 rounded-none bg-destructive text-destructive-foreground font-mono text-xs uppercase">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-md bg-[#1A1A1A] text-[#FFFFFF] hover:bg-black transition-all duration-300 font-sans font-bold tracking-widest uppercase py-6 mt-8 flex items-center justify-center gap-2 border border-white/10">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            PROCESSING...
          </> : <>
            REQUEST CONSULTATION
            <ArrowRight className="size-4 text-[#FFFFFF]" />
          </>}
      </Button>
    </Form>;
}
export default function InquiryForm() {
  return <section id="inquiryform" className="bg-background text-foreground py-16 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-0 right-0 w-px h-full bg-border/20" />
        <div className="absolute top-0 left-1/2 w-px h-full bg-border/10" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div className="lg:col-span-5 space-y-12">
            <div className="space-y-6">
              <h2 className="text-4xl md:text-6xl font-black tracking-tighter font-sans uppercase leading-none text-foreground border-l-8 border-primary pl-6">
                CONSULT <br />SOLIDTRUST <br />BUILDERS
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed font-sans max-w-md">Expert builders in North West London specializing in luxury loft conversions, bespoke home extensions, and high-end property renovations. Contact our&nbsp; team to discuss your blueprint.</p>
            </div>

            <div className="space-y-8 pt-8 border-t border-border">
              <div className="flex items-start gap-6">
                <div className="mt-1 bg-primary text-primary-foreground p-3 rounded-none">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h4 className="font-sans font-bold uppercase tracking-widest text-xs text-foreground">Direct Line</h4>
                  <p className="font-mono text-lg mt-1">+44 7810 664982</p>
                  <p className="text-[10px] font-mono uppercase text-muted-foreground mt-1 tracking-wider">Mon-SUN: 08:00 - 21:00</p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="mt-1 bg-primary text-primary-foreground p-3 rounded-none">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h4 className="font-sans font-bold uppercase tracking-widest text-xs text-foreground">EMAIL</h4>
                  <p className="font-mono text-lg mt-1">INFO@SOLIDTRUSTBUILDER.COM</p>
                  <p className="text-[10px] font-mono uppercase text-muted-foreground mt-1 tracking-wider">Project response within 12 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="mt-1 bg-primary text-primary-foreground p-3 rounded-none">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h4 className="font-sans font-bold uppercase tracking-widest text-xs text-foreground">NW London HQ</h4>
                  <p className="font-mono text-lg mt-1">WEMBLEY PARK, LONDON HA9</p>
                  <p className="text-[10px] font-mono uppercase text-muted-foreground mt-1 tracking-wider">Serving North West London & Beyond</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="rounded-lg border-none bg-[#1A1A1A] shadow-none p-8 gap-0">
              <CardHeader className="p-0 mb-6 border-none">
                <CardTitle className="text-3xl font-bold font-sans text-[#FFFFFF]">Project Inquiry</CardTitle>
                <CardDescription className="text-base font-sans text-[#B0B0B0] mt-2">Fill out the details below and our team will contact you within 24 hours.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <FormProvider formKey="lead_generation_form_leadgeneration" formId="lead_generation_form_leadgeneration" sectionName="InquiryForm" defaultValues={{
                name: "",
                email: "",
                phone: "",
                inquiry_type: "",
                subject: "",
                message: ""
              }} submitText="REQUEST CONSULTATION" successMessage="Project details received. Our lead surveyor will contact you shortly to arrange a site visit." errorMessage="Structural error in form submission. Please verify details and re-submit.">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}